import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { ActionAPI, FieldAPI, FormAPI, ModalProps, ModalService, RadioGroupAPI, SelectableItem, ToastService } from '@philips-internal/oswald-ui-components';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';

import { Inject } from '@odinjs/odin';
import moment from 'moment';

import BaseController from '../../../BaseController';
import { FragmentNames, HealthHistoryDiseasesViewerAliases } from '../../../../codegen';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../constants/routes';
import { HealthHistoryDiseasesRequest, HealthHistoryDiseasesResponse, HealthHistoryType } from '../../health-history-types';
import { BooleanUtils } from '../../../../../src/utils/boolean-utils';
import { DiseasesHighlightIntensitySeverityId, DiseasesRadioGroupId } from '../radio-group-data/diseases-radio-group-expressions';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants/lib';
import DialogService from '../../../../services/dialog-service';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { FunctionName, PEP_281Parameters } from '../../../../utils/parameters';
import { HealthProfessionalUser } from '../../../../app/authentication';

enum RadioGroupOptions {
  MILD_LITTLE          = 0,
  MEDIUM_MODERATE      = 1,
  SEVERE_LARGE         = 2
}

enum VerifyReviewParameter {
  ALLOWED_REVIEW = 'S',
  NOT_ALLOWED_REVIEW = 'N'
}

@Controller({ fragment: FragmentNames.HEALTH_HISTORY_DISEASES_VIEWER })
export default class HealthHistoryDiseasesViewerController extends BaseController {

  @Inject(HealthHistoryDiseasesViewerAliases.ACTION_NEW_HEALTH_HISTORY_DISEASES)
  private actionNewHealthHistoryAPI!: ActionAPI

  @Inject(HealthHistoryDiseasesViewerAliases.ACTION_INACTIVATE_HEALTH_HISTORY_DISEASES)
  private actionInactivateHealthHistoryAPI!: ActionAPI

  @Inject(HealthHistoryDiseasesViewerAliases.ACTION_REVIEW_HEALTH_HISTORY_DISEASES)
  private actionReviewHealthHistoryAPI!: ActionAPI

  @Inject(HealthHistoryDiseasesViewerAliases.FORM_HEALTH_HISTORY_DISEASES_VIEWER)
  private formHealthHistoryDiseasesAPI!: FormAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_DISEASES_TEXT_FREE)
  private fieldDiseaseFreeAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_CID)
  private fieldCidAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_SAE)
  private fieldSaeAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_SITUATION)
  private fieldSituationAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_SECURITY_LEVEL)
  private fieldSecurityLevelAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_START_DATE)
  private fieldStartDateAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_END_DATE)
  private fieldEndDateAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_PATHOLOGIE)
  private fieldPathologyAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_INSERT_PROBLEMS_LIST)
  private fieldInsertProblemsAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_INSERT_DIAGNOSIS_LIST)
  private fieldInsertDiagnosisAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_APPROACH)
  private fieldApproachAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_NOTES)
  private fieldNotesAPI!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_REVIEW_DATE)
  private fieldReviewDateApi!: FieldAPI;

  @Inject(HealthHistoryDiseasesViewerAliases.FIELD_REVIEW_USERNAME)
  private fieldReviewUsernameApi!: FieldAPI;

  @OnMount({ target: HealthHistoryDiseasesViewerAliases.LAYOUT_HEALTH_HISTORY_DISEASES_VIEWER })
  handleMountLayout():void {

    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    InformationBarUtils.collapsedBar = true;
    this.context.set({encounterId, patientId});
    HealthHistoryListContext.currentTab = HealthHistoryType.DISEASES;
  }

  @OnMount({ target: HealthHistoryDiseasesViewerAliases.FORM_HEALTH_HISTORY_DISEASES_VIEWER })
  handleMountForm(): void {
    this.formHealthHistoryDiseasesAPI.setLoading(true);
    this.fetchHealthHistoryDiseases()
  }

  @OnMount({ target: HealthHistoryDiseasesViewerAliases.RADIO_GROUP_HIGHLIGHT_INTENSITY_SEVERITY })
  handleMountRadioGroup(event: MountEvent): void {
    const radioGroup = event.target as RadioGroupAPI;
    this.setRadioGroupValues(DiseasesRadioGroupId.HIGHLIGHT_INTENSITY_SEVERITY, radioGroup);
  }

  @OnUnmount(HealthHistoryDiseasesViewerAliases.LAYOUT_HEALTH_HISTORY_DISEASES_VIEWER)
  handleUnmountLayout(): void {
    HealthHistoryListContext.clean();
    HealthHistoryListContext.currentTab = HealthHistoryType.DISEASES;
  }

  private getParamAccess(module: string, parameter: string): string {
    return FoundationUtils.findModuleParam(module, parameter) as string;
  }

  private defineEnablingReviewItem(): void {
    const isEnabledReview = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.ALLOW_REVIEW_HEALTH_HISTORY);
    const allowInfo = isEnabledReview === VerifyReviewParameter.ALLOWED_REVIEW && InformationBarUtils.allowInfo && HealthHistoryListContext.isAllowEdit;
    this.actionReviewHealthHistoryAPI.setEnabled(allowInfo);
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
  }

  private review(idHealthHistory: number): void {
    const idHealthHistoryDiseases = idHealthHistory;
    this.formHealthHistoryDiseasesAPI.setLoading(true);
    HttpService.put(Endpoint.REVIEW_HEALTH_HISTORY_DISEASES, { idHealthHistoryDiseases })
      .then(() => {
        this.formHealthHistoryDiseasesAPI.setLoading(false);
        this.goBack();
        this.showToast('text.successfully-reviewed', ToastLevel.SUCCESS);
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
        this.formHealthHistoryDiseasesAPI.setLoading(false);
      });
  }

  private confirmReview(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: '',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.review(idHealthHistory); },
      titleDialog: 'text.confirm-review-item',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'text.review-item-hh',
    });
  }

  @OnClick({ target: HealthHistoryDiseasesViewerAliases.ACTION_NEW_HEALTH_HISTORY_DISEASES })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.HEALTH_HISTORY_DISEASES_CREATE, {
      encounterId,
      patientId,
    });
  }

  @OnClick({ target: HealthHistoryDiseasesViewerAliases.ACTION_REVIEW_HEALTH_HISTORY_DISEASES })
  handleClickReview(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.confirmReview(idHealthHistory as number);
  }

  @OnClick({ target: HealthHistoryDiseasesViewerAliases.ACTION_INACTIVATE_HEALTH_HISTORY_DISEASES })
  handleCLickInactivate(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'health-history-inactivate',
      id: 'health-history-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {idHealthHistory} as Record<string, Value>;
    ModalService.openDefault(modalProperties);
  }

  private handleVisibleFields(record: HealthHistoryDiseasesResponse): void {
    this.fieldCidAPI.setVisible(record.disease?.diagnosisDescription != null);
    this.fieldDiseaseFreeAPI.setVisible(record.diseaseAvailableDescription != null);
    this.fieldSaeAPI.setVisible(record.sae?.description != null);
    this.fieldSituationAPI.setVisible(record.status?.description != null);
    this.fieldSecurityLevelAPI.setVisible(record.securityLevel?.description != null);
    this.fieldStartDateAPI.setVisible(record.startDate != null);
    this.fieldEndDateAPI.setVisible(record.endDate != null);
    this.fieldPathologyAPI.setVisible(record.pathology?.description != null);
    this.fieldInsertProblemsAPI.setVisible(record.insertProblemsList != null);
    this.fieldInsertDiagnosisAPI.setVisible(record.insertDiagnosis != null);
    this.fieldApproachAPI.setVisible(record.approachDescription != null);
    this.fieldNotesAPI.setVisible(record.notes != null);
    this.fieldReviewDateApi.setVisible(record.reviewDate != null);
    this.fieldReviewUsernameApi.setVisible(record.reviewUsername != null);
  }

  private fetchHealthHistoryDiseases(): Promise<void> {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo && HealthHistoryListContext.isAllowEdit;

    return HttpService.get(Endpoint.HEALTH_HISTORY_DISEASES, { sequenceNumberDisease: idHealthHistory })
      .then(res => {
        const record = res.body as HealthHistoryDiseasesResponse;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const formData = this.convertToValidType(record);
        const highlightIntensitySeverity = {key: record.highlightIntensitySeverity, label: '', value: record.highlightIntensitySeverity };
        const showInactivate = record.professional?.id === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

        this.context.set({ healthHistoryDiseases: formData });

        this.formHealthHistoryDiseasesAPI.setEditing(true);
        this.formHealthHistoryDiseasesAPI.validate();
        this.formHealthHistoryDiseasesAPI.setDisabled(true);
        this.formHealthHistoryDiseasesAPI.setData({ ...formData, highlightIntensitySeverity });
        this.actionInactivateHealthHistoryAPI.setEnabled(showInactivate);
        this.handleVisibleFields(record);

      }).catch((err) => err?.body?.errorMessage && this.showToast(err?.body, ToastLevel.ERROR))
      .finally(() => {
        this.formHealthHistoryDiseasesAPI.setLoading(false);
        this.actionNewHealthHistoryAPI.setEnabled(allowInfo);
        this.defineEnablingReviewItem();
      });
  }

  private convertToValidType(healthHistoryDiseases: HealthHistoryDiseasesResponse): HealthHistoryDiseasesRequest {
    return {
      approachDescription:healthHistoryDiseases.approachDescription || '',
      classification: healthHistoryDiseases.classification || '',
      condition: healthHistoryDiseases.condition || '',
      deniesDiseases: BooleanUtils.isTrueValue(healthHistoryDiseases.deniesDiseases),
      disease: healthHistoryDiseases.disease?.diagnosisDescription || '',
      diseaseAvailableDescription: healthHistoryDiseases.diseaseAvailableDescription || '',
      displayPatientAlerts: BooleanUtils.isTrueValue(healthHistoryDiseases.displayPatientAlerts),
      endDate: healthHistoryDiseases.endDate ? moment(healthHistoryDiseases.endDate).utc().format('L') : '',
      highlightIntensitySeverity: healthHistoryDiseases.highlightIntensitySeverity || '',
      idHealthHistory: healthHistoryDiseases.idHealthHistory,
      insertDiagnosis: BooleanUtils.isTrueValue(healthHistoryDiseases.insertDiagnosis),
      insertProblemsList: BooleanUtils.isTrueValue(healthHistoryDiseases.insertProblemsList),
      notes: healthHistoryDiseases.notes || '',
      pathology: healthHistoryDiseases.pathology?.description || '',
      recordDate: healthHistoryDiseases.recordDate ? DateFormatterUtil.timeStamp(String(healthHistoryDiseases.recordDate)) : '',
      reviewDate: healthHistoryDiseases?.reviewDate ? DateFormatterUtil.timeStamp(String(healthHistoryDiseases.reviewDate)) : '',
      reviewUsername: healthHistoryDiseases?.reviewUsername || '',
      sae: healthHistoryDiseases.sae?.description || '',
      securityLevelDescription: healthHistoryDiseases.securityLevel ? healthHistoryDiseases.securityLevel.description : '',
      startDate: healthHistoryDiseases.startDate ? moment(healthHistoryDiseases.startDate).utc().format('L') : '',
      status:healthHistoryDiseases.status?.description || '',
    }
  }

  private treatRadioGroupData(responseBody: SelectableItem[], radioGroup: RadioGroupAPI): void {
    const { highlightIntensitySeverity } = this.context.get('healthHistoryDiseases') as HealthHistoryDiseasesRequest;
    switch (highlightIntensitySeverity) {
      case  DiseasesHighlightIntensitySeverityId.MEDIUM_MODERATE :
        radioGroup.setValue(responseBody[RadioGroupOptions.MEDIUM_MODERATE])
        break;
      case  DiseasesHighlightIntensitySeverityId.SEVERE_LARGE :
        radioGroup.setValue(responseBody[RadioGroupOptions.SEVERE_LARGE])
        break;
      default:
        radioGroup.setValue(responseBody[RadioGroupOptions.MILD_LITTLE])
        break;
    }
  }
  private setRadioGroupValues(expressionId: number, radioGroup: RadioGroupAPI): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);
        this.treatRadioGroupData(responseBody, radioGroup);
      }).catch((error) => {
        error?.body?.errorMessage && this.showToast(error?.body, ToastLevel.ERROR)
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-diseases-viewer',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}
